import {
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  PROFILE_AVAILABILITY_STATUS,
  normalizeWhatMattersMost
} from "../../../constants/workerOnboarding";

function stripSort(bank) {
  return { ...bank, sortCode: (bank.sortCode || "").replace(/-/g, "") };
}

export function buildWorkerProfileFormData(values, certsData) {
  const fd = new FormData();
  fd.append("role", "worker");

  const normalizePlus = (v) => (v ? `+${String(v).replace(/^\+/, "")}` : "");

  const pi = values.personalInformation || {};
  const line1 = (pi.addressLine1 || "").trim();
  const line2 = (pi.addressLine2 || "").trim();
  const city = (pi.city || "").trim();
  const county = (pi.county || "").trim();
  const pc = (pi.postcode || "").trim();
  const country = (pi.country || "").trim();
  const tail = [city, county, pc, country].filter(Boolean).join(", ");
  const currentAddressCombined =
    [line1, line2, tail].filter(Boolean).join(", ") || "N/A";

  const personalInformation = {
    firstName: pi.firstName ?? "",
    surname: pi.lastName ?? "",
    fullName: pi.firstName ?? "",
    dob: pi.dob ?? "",
    gender: pi.gender ?? "",
    currentAddress: currentAddressCombined,
    road: line1 || "N/A",
    door: line2 || "N/A",
    addressLine1: line1 || "N/A",
    city: city || "N/A",
    county: county || "",
    postalCode: pc || "N/A",
    country: country || "N/A",
    phoneNumber: normalizePlus(pi.phoneNumber),
    email: pi.email ?? "",
    nationalInsuranceNumber: (pi.nationalInsuranceNumber || "").trim(),
    nationality: pi.nationality ?? "",
    baseLat: pi.baseLat ?? "",
    baseLng: pi.baseLng ?? ""
  };

  const ec = values.emergencyContact || {};
  const emergencyContact = {
    fullName: ec.firstName ?? "",
    surname: ec.lastName ?? "",
    relationshipToWorker: ec.relationshipToWorker || "",
    phoneNumber: normalizePlus(ec.phoneNumber),
    ...(ec.alternativePhoneNumber?.trim?.()
      ? { alternativePhoneNumber: normalizePlus(ec.alternativePhoneNumber) }
      : {})
  };

  const rtwVals = values?.rightToWork || {};
  const rightToWorkStatus = rtwVals.rightToWorkStatus || "";
  const isBritishOrIrish = rightToWorkStatus === RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
  const rightToWork = {
    rightToWorkInUk: true,
    isBritishOrIrish,
    rightToWorkStatus,
    homeShareSortCode: isBritishOrIrish ? "" : rtwVals.shareCode || "",
    visaExpiryDate: isBritishOrIrish ? "" : rtwVals.visaExpiryDate || "",
    dvlaShareCode: rtwVals.dvlaShareCode || "",
    drivingLicenseExpiryDate: rtwVals.drivingLicenseExpiryDate || "",
    licenseNumber: rtwVals.licenseNumber || "",
  };

  const mapRoleSlug = (r) => {
    if (!r) return null;
    if (typeof r === "string") return r;
    return r.roleSlug || r.value || r.slug || r.id || String(r);
  };

  const whatMattersMost = normalizeWhatMattersMost(
    values?.workPreferences?.whatMattersMost
  );

  const langs = values?.personalInformation?.languagesSpoken;
  const workPreferences = {
    ...values.workPreferences,
    languages: Array.isArray(langs) ? langs : [],
    preferredWorkRadius: Number(values?.workPreferences?.preferredWorkRadius ?? 0),
    optOut48HourLimit: !!values?.workPreferences?.optOut48HourLimit,
    whatMattersMost,
    sectors: (values?.workPreferences?.sectors || []).map((sec) => ({
      sectorSlug: sec?.sectorSlug || sec?.value || String(sec?.sectorSlug || ""),
      roles: (sec?.roles || [])
        .map(mapRoleSlug)
        .filter(Boolean)
        .map((roleSlug) => ({ roleSlug, isEligible: false }))
    }))
  };

  fd.append("personalInformation", JSON.stringify(personalInformation));
  fd.append("emergencyContact", JSON.stringify(emergencyContact));
  fd.append("rightToWork", JSON.stringify(rightToWork));

  const hasP45 = values?.employmentTax?.hasP45 === "yes";
  const employmentTaxData = {
    hasP45,
    starterChecklistChoice: !hasP45
      ? values?.employmentTax?.starterChecklistChoice || ""
      : "",
    hasPostgraduateLoan: !hasP45
      ? values?.employmentTax?.hasPostgraduateLoan === "yes"
      : false,
    planType:
      !hasP45 && values?.employmentTax?.hasPostgraduateLoan === "yes"
        ? values?.employmentTax?.planType || ""
        : "",
    notesField: (values?.employmentTax?.notes || "").trim(),
    acceptEmergencyTaxCode: !!values?.employmentTax?.acceptEmergencyTaxCode,
  };
  fd.append("employmentTax", JSON.stringify(employmentTaxData));
  fd.append("bankDetails", JSON.stringify(stripSort(values.bankDetails || {})));
  fd.append(
    "moreAboutYou",
    JSON.stringify({
      healthAccessibilityAdjustments:
        values?.moreAboutYou?.healthAccessibilityDetails?.trim() || "",
      aboutYouAndWorkHistory: values?.moreAboutYou?.aboutYouWorkHistory?.trim() || "",
      hasUnspentConvictionsOrPendingCases: !!values?.moreAboutYou?.hasConvictionsOrPending,
      criminalConvictionsDetails:
        values?.moreAboutYou?.criminalConvictionsDetails?.trim() || ""
    })
  );
  fd.append("workPreferences", JSON.stringify(workPreferences));

  const pa = values?.profileAvailability || {};
  const status = pa.status || PROFILE_AVAILABILITY_STATUS.AVAILABLE;
  fd.append("status", status);
  if (status === PROFILE_AVAILABILITY_STATUS.UNAVAILABLE) {
    fd.append("unavailableTill", pa.unavailableTill || "");
  }

  const dbsCerts = [];
  const otherCerts = [];
  const filesByFieldKey = {};

  (values?.certificateEntries || []).forEach((entry) => {
    if (!entry?.certSlug) return;
    const cert = certsData.find((c) => c.slug === entry.certSlug);
    if (!cert) return;
    const hasFile = entry?.file instanceof File;
    const hasServiceNumber = !!entry?.serviceNumber?.trim?.();
    if (!hasFile && !hasServiceNumber) return;

    const fieldKey = cert.titleToUse;
    if (cert.hasServiceNumber) {
      const dbsEntry = { certSlug: cert.slug };
      if (hasServiceNumber) {
        dbsEntry.serviceNumber = String(entry.serviceNumber).trim();
      }
      dbsCerts.push(dbsEntry);
      if (hasFile) {
        if (!filesByFieldKey[fieldKey]) filesByFieldKey[fieldKey] = [];
        filesByFieldKey[fieldKey].push(entry.file);
      }
    } else if (hasFile) {
      otherCerts.push({ certSlug: cert.slug });
      if (!filesByFieldKey[fieldKey]) filesByFieldKey[fieldKey] = [];
      filesByFieldKey[fieldKey].push(entry.file);
    }
  });

  const complianceSkillsData = {};
  if (dbsCerts.length > 0) complianceSkillsData.dbs = dbsCerts;
  if (otherCerts.length > 0) complianceSkillsData.otherCerts = otherCerts;
  fd.append("complianceSkills", JSON.stringify(complianceSkillsData));

  const appendOne = (key, f) => {
    if (!f) return;
    if (f instanceof File || (typeof Blob !== "undefined" && f instanceof Blob)) {
      fd.append(key, f, f.name || undefined);
    }
  };
  const appendMany = (key, arr) => (arr || []).forEach((item) => appendOne(key, item));

  appendMany("uploadId", values?.rightToWork?.uploadId);
  appendOne("uploadSelfie", values?.personalInformation?.jobarkIdSelfie);
  appendMany("proofOfAddress", values?.rightToWork?.proofOfAddress);
  appendOne("drivingLicense", values?.rightToWork?.drivingLicense);
  appendOne("drivingLicenseBack", values?.rightToWork?.drivingLicenseBack);

  appendMany(
    "p45File",
    Array.isArray(values?.employmentTax?.p45Upload)
      ? values.employmentTax.p45Upload
      : values?.employmentTax?.p45Upload
      ? [values.employmentTax.p45Upload]
      : []
  );

  Object.entries(filesByFieldKey).forEach(([fieldKey, files]) => {
    appendMany(fieldKey, files);
  });

  return fd;
}
